import type { CampaignOfferFacts } from '../../types/campaign.js';

// FRANCHISE CAMPAIGN COPY — THE DISTILLED OFFER FACTS.
//
// ⚠️⚠️ WHY THIS EXISTS, MEASURED. A real Fitstop "Winter Reset" toolkit is 30
// pages and 3.3MB, and cost 58,714 input tokens in a single call — roughly 1,957
// per page. The model read it WELL (correct trigger codes, the 28-day/$149
// mechanics, the tier names, the dates), so quality was never the problem. The
// problem was that documents ride the USER turn, which sits outside the cached
// system prefix, so that 58k was re-billed on every generation and every
// refinement.
//
// The fix is to read it ONCE into a compact factual brief and use that
// everywhere afterwards. A 30-page toolkit becomes a few hundred tokens.
//
// ⚠️⚠️ BUT THE REAL WIN IS CORRECTNESS, NOT COST, AND THAT IS WHY THE BLOCK IS
// EDITABLE. If the model misreads a pricing tier out of thirty pages, the account
// manager corrects it ONCE and every generation afterwards is right. A raw
// attachment gives nobody a place to catch that — the misreading just recurs,
// invisibly, on every run.
//
// ⚠️ RE-READING IS AN EXPLICIT ACTION. Nothing here refreshes on its own:
// reopening a workspace must not silently re-bill 58k tokens. `sources` records
// the links it was read from, so a changed link set surfaces as STALE rather
// than being silently ignored.

/**
 * The block rides EVERY generation and EVERY refinement, so it is bounded.
 * ~1,500 tokens: comfortably more than a distilled 30-page toolkit needs, and
 * far short of the raw document it replaces.
 */
export const MAX_OFFER_FACTS_CHARS = 6_000;

/**
 * Trim, cap, and collapse blank to null so "absent" and "empty" are ONE state.
 *
 * ⚠️ That collapse matters at the prompt for the same reason it does for the
 * voice profile: an empty labelled block reads to a model as "there are no offer
 * facts", which is a different and worse instruction than saying nothing at all.
 */
export function normalizeOfferFactsText(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  if (trimmed === '') return null;
  return trimmed.slice(0, MAX_OFFER_FACTS_CHARS);
}

/** Same normalisation over the whole stored record; null when there is nothing to say. */
export function normalizeOfferFacts(
  facts: CampaignOfferFacts | null | undefined,
): CampaignOfferFacts | null {
  if (!facts) return null;
  const text = normalizeOfferFactsText(facts.text);
  if (text === null) return null;
  return {
    text,
    sources: (facts.sources ?? []).map((s) => (s ?? '').trim()).filter((s) => s !== ''),
    distilledAt: facts.distilledAt,
    model: facts.model,
  };
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((value, index) => value === right[index]);
}

/**
 * Whether the block was read from a different set of links than the ones
 * currently in the workspace.
 *
 * ⚠️ SURFACED, NOT ACTED ON. A stale block is still used — silently ignoring the
 * AM's edited facts because they added a link would be worse — but the workspace
 * says so and offers the explicit re-read. Auto-re-reading here is precisely the
 * "reopening the workspace re-bills 58k tokens" behaviour this design refuses.
 */
export function offerFactsAreStale(
  facts: CampaignOfferFacts | null,
  currentDocumentLinks: readonly string[],
): boolean {
  if (!facts) return false;
  return !sameSet(facts.sources, currentDocumentLinks);
}

/** "Read from 2 documents on 27 Aug 2026" — provenance, in one line. */
export function describeOfferFacts(facts: CampaignOfferFacts): string {
  const count = facts.sources.length;
  const when = new Date(facts.distilledAt);
  const date = Number.isNaN(when.getTime())
    ? 'an unknown date'
    : when.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const what = count === 1 ? '1 document' : `${count} documents`;
  return `Read from ${what} on ${date}.`;
}
